import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, History, Undo2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AREAS, ROLE_LABEL } from "@/lib/roles";

const AREA_LABEL = { ...Object.fromEntries(AREAS), team: "Team", account: "Account" };
const FILTERS = [["all", "All"], ...AREAS, ["team", "Team"], ["account", "Account"]];

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

export const ActivityPanel = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [area, setArea] = useState("all");
  const { refresh } = useSite();
  const load = useCallback(() => api.get("/activity").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([])), []);
  useEffect(() => {
    load();
  }, [load]);
  const undo = async (r) => {
    if (!window.confirm(`Undo this change?\n\n${r.summary}`)) return;
    try {
      await api.post(`/activity/${r.id}/undo`);
      toast.success("Change undone — restored on the website");
      load();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const sendNow = async () => {
    try {
      await api.post("/activity/summary");
      toast.success("Weekly summary sent");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const shown = useMemo(() => {
    const s = q.toLowerCase();
    return rows.filter((r) => (area === "all" || r.area === area) && `${r.user_name} ${r.user_email} ${r.summary}`.toLowerCase().includes(s));
  }, [rows, q, area]);

  return (
    <div data-testid="activity-panel">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <label className="flex flex-1 items-center gap-2 rounded-full border border-[#F4EEE2]/15 px-4 py-2.5">
          <Search className="h-4 w-4 text-[#B7BFCC]" />
          <input data-testid="activity-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by person or change…" className="w-full bg-transparent text-sm text-[#F4EEE2] outline-none placeholder:text-[#9AA4B4]" />
        </label>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`activity-filter-${k}`} onClick={() => setArea(k)} className={`rounded-full px-3 py-2 font-ui text-xs ${area === k ? "bg-[#F4EEE2] font-semibold text-[#0B111E]" : "text-[#F4EEE2]/80 hover:bg-[#F4EEE2]/5"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="activity-send-summary-btn" onClick={sendNow} className="flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2.5 font-ui text-xs font-semibold text-[#F4EEE2] hover:bg-[#D4AF37]/10">
          <Mail className="h-3.5 w-3.5" /> Email summary now
        </button>
      </div>
      <ol className="relative mt-8 border-l border-[#D4AF37]/25 pl-6">
        {shown.map((r, i) => (
          <li key={`${r.at}-${i}`} data-testid={`activity-row-${i}`} className="relative pb-6">
            <span className="absolute -left-[31px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#D4AF37] ring-4 ring-[#0B111E]" />
            <p className="font-ui text-sm text-[#F4EEE2]">
              <strong>{r.user_name}</strong> <span className="text-[#9AA4B4]">· {ROLE_LABEL[r.role] || r.role}</span>
            </p>
            <p className="mt-0.5 text-sm leading-relaxed text-[#C9D0DB]">{r.summary}</p>
            <p className="mt-1 font-mono text-[11px] text-[#9AA4B4]">{when(r.at)} · <span className="text-[#D4AF37]">{AREA_LABEL[r.area] || r.area}</span>{r.undone_at && <span className="text-[#F2A08A]"> · undone by {r.undone_by}</span>}</p>
            {r.can_undo && (
              <button data-testid={`activity-undo-${i}`} onClick={() => undo(r)} className="mt-2 flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 px-3 py-1.5 font-ui text-xs text-[#F4EEE2] hover:bg-[#D4AF37]/10">
                <Undo2 className="h-3.5 w-3.5" /> Undo
              </button>
            )}
          </li>
        ))}
      </ol>
      {!shown.length && (
        <p className="flex items-center justify-center gap-2 py-10 text-sm text-[#B7BFCC]"><History className="h-4 w-4" /> No activity yet. Changes your team makes will appear here.</p>
      )}
    </div>
  );
};
